import { MouseEvent, useState } from 'react';

import {
  faArchive,
  faCabinetFiling,
  faCopy,
  faEllipsisV,
  faPencil,
  faPlus,
  faTrashAlt
} from '@fortawesome/pro-light-svg-icons';
import { usePlanUnitsOptions } from 'hooks';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';

import { PlanUnitMix } from 'components/types';

import { deleteScenario, updateScenario } from 'RestAPIs/scenario';

import { scenarioAtom } from '../../atoms';

import {
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem
} from '@mui/material';

import {
  ConfirmationDialog,
  FontAwesomeSvgIcon,
  showSnackbar
} from 'components/shared';

import AddEditScenarioDialog, { ScenarioForm } from './AddEditScenarioDialog';
import ScenarioArchiveDialog from './ScenarioArchiveDialog';

type Props = {
  onSaveScenario: (id: number) => void;
};

const ScenarioActions = ({ onSaveScenario: _onSaveScenario }: Props) => {
  const { t } = useTranslation('common');
  const [selectedScenario, setSelectedScenario] = useRecoilState(scenarioAtom);
  const [isAddEditOpen, setIsAddEditOpen] = useState<boolean>(false);
  const [isArchiveOpen, setIsArchiveOpen] = useState<boolean>(false);
  const [dataForEdit, setDataForEdit] = useState<ScenarioForm | null>(null);
  const [scenarioToCopyId, setScenarioToCopyId] = useState<number | null>(null);
  const [shouldShowDeleteConfirmation, setShouldShowDeleteConfirmation] =
    useState<boolean>(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const allPlanUnits = usePlanUnitsOptions({
    showGroups: false
  }) as PlanUnitMix[];

  const handleCloseAddEdit = () => {
    setIsAddEditOpen(false);
    setDataForEdit(null);
    setScenarioToCopyId(null);
  };

  const handleCloseArchiveDialog = () => {
    setIsArchiveOpen(false);
  };

  const closeMenu = () => {
    setMenuAnchorEl(null);
  };

  const onClickAddScenario = () => {
    setIsAddEditOpen(true);
  };

  const onClickArchiveScenario = async () => {
    if (!selectedScenario) {
      return false;
    }

    const data = {
      ...selectedScenario,
      archived: true
    };

    try {
      await updateScenario(data, selectedScenario.scenarioId);
      showSnackbar({
        severity: 'success',
        message: t('scenario.archived')
      });

      setSelectedScenario(null);

      _onSaveScenario(selectedScenario.scenarioId);

      closeMenu();
      return true;
    } catch {
      showSnackbar({
        severity: 'error',
        message: t('somethingWentWrong')
      });
      return false;
    }
  };

  const onClickCopyScenario = () => {
    if (!selectedScenario) {
      return;
    }

    const copiedData = {
      ...selectedScenario,
      costCenters: allPlanUnits.filter(({ id }) =>
        selectedScenario.costCenters.map(({ id }) => id).includes(id)
      )
    } as ScenarioForm;

    delete copiedData.scenarioId;

    setDataForEdit(copiedData);
    setScenarioToCopyId(selectedScenario.scenarioId);
    setIsAddEditOpen(true);
    closeMenu();
  };

  const onClickDeleteScenario = () => {
    setDataForEdit(null);
    setShouldShowDeleteConfirmation(true);
  };

  const onClickEditScenario = () => {
    if (!selectedScenario) {
      return;
    }

    setDataForEdit({
      ...selectedScenario,
      costCenters: allPlanUnits.filter(({ id }) =>
        selectedScenario.costCenters.map(({ id }) => id).includes(id)
      ),
      isDefault: !!selectedScenario.isDefault
    });

    setIsAddEditOpen(true);
    closeMenu();
  };

  const onClickMenu = (evt: MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(evt.currentTarget);
  };

  const onClickShowArchive = () => {
    setIsArchiveOpen(true);
    closeMenu();
  };

  const onDeleteConfirmation = async () => {
    if (!selectedScenario) {
      return false;
    }

    try {
      await deleteScenario(selectedScenario.scenarioId);
      showSnackbar({
        severity: 'success',
        message: t('scenario.deleted')
      });

      setSelectedScenario(null);
      closeMenu();

      _onSaveScenario(selectedScenario.scenarioId);
      return true;
    } catch {
      showSnackbar({
        severity: 'error',
        message: t('somethingWentWrong')
      });
      return false;
    } finally {
      setShouldShowDeleteConfirmation(false);
    }
  };

  const onSaveScenario = (scenarioId: number) => {
    setSelectedScenario(null);
    _onSaveScenario(scenarioId);
  };

  return (
    <Box ml={1}>
      <IconButton onClick={onClickMenu} size="small">
        <FontAwesomeSvgIcon icon={faEllipsisV} />
      </IconButton>
      <Menu
        open={!!menuAnchorEl}
        anchorEl={menuAnchorEl}
        onClose={closeMenu}
        MenuListProps={{
          sx: { width: 260 }
        }}
      >
        <MenuItem onClick={onClickAddScenario}>
          <ListItemIcon>
            <FontAwesomeSvgIcon icon={faPlus} fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('scenario.add')}</ListItemText>
        </MenuItem>
        <MenuItem disabled={!selectedScenario} onClick={onClickEditScenario}>
          <ListItemIcon>
            <FontAwesomeSvgIcon icon={faPencil} fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('scenario.edit')}</ListItemText>
        </MenuItem>
        <MenuItem disabled={!selectedScenario} onClick={onClickCopyScenario}>
          <ListItemIcon>
            <FontAwesomeSvgIcon icon={faCopy} fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('scenario.copy')}</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={!selectedScenario || selectedScenario.isDefault}
          onClick={onClickDeleteScenario}
        >
          <ListItemIcon>
            <FontAwesomeSvgIcon icon={faTrashAlt} fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('scenario.delete')}</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={!selectedScenario || selectedScenario.isDefault}
          onClick={onClickArchiveScenario}
        >
          <ListItemIcon>
            <FontAwesomeSvgIcon icon={faArchive} fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('scenario.archiveScenario')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={onClickShowArchive}>
          <ListItemIcon>
            <FontAwesomeSvgIcon icon={faCabinetFiling} fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('scenario.archive')}</ListItemText>
        </MenuItem>
      </Menu>

      {isAddEditOpen && (
        <AddEditScenarioDialog
          scenario={dataForEdit}
          isOpen={isAddEditOpen}
          handleClose={handleCloseAddEdit}
          onSave={onSaveScenario}
          scenarioToCopyId={scenarioToCopyId}
        />
      )}
      {shouldShowDeleteConfirmation && (
        <ConfirmationDialog
          handleAction={onDeleteConfirmation}
          cancelBtnText={t('no')}
          confirmBtnText={t('yes')}
          type="warning"
          title={t('scenario.confirmationTitle')}
        >
          <p>{t('scenario.confirmationBody')}</p>
        </ConfirmationDialog>
      )}
      {isArchiveOpen && (
        <ScenarioArchiveDialog
          handleClose={handleCloseArchiveDialog}
          isOpen={isArchiveOpen}
        />
      )}
    </Box>
  );
};

export default ScenarioActions;
